import { KeyboardEvent, ReactNode, useId, useRef } from "react";
import { Chip } from "../Chip";
import { useNextT } from "../../i18n";
import s from "./ToggleChips.module.scss";

/** `icon`: decorative, shown before the words while the chip is not chosen (e.g. a reaction's colour dot). */
export type ToggleChipOption = { value: string; label: string; icon?: ReactNode };

/** `labelledBy`: an existing heading names the group instead of the visible label (e.g. a form section's h2). */
type Common = { label: string; options: ToggleChipOption[]; labelledBy?: string };
export type ToggleChipsProps = Common & (
  | { multiple: true; value: string[]; onChange: (value: string[]) => void }
  // allowNone: tapping the chosen chip clears the choice (onChange("")); the arrow keys still only move and choose.
  | { multiple: false; value: string; onChange: (value: string) => void; allowNone?: boolean }
);

/**
 * A row of chips under a visible label. `multiple`: on/off chips (`aria-pressed`, e.g. categories).
 * Single: a chip radio group (e.g. visit schedule) — one tab stop, arrow keys move and choose (mirrored in Hebrew), Home/End.
 */
export const ToggleChips = (props: ToggleChipsProps) => {
  const id = useId();
  const { isRtl } = useNextT();
  const group = useRef<HTMLDivElement>(null);
  const { label, options, labelledBy } = props;
  const name = labelledBy ?? id;
  const head = labelledBy ? null : <span id={id} className={s.label}>{label}</span>;

  if (props.multiple) {
    const { value, onChange } = props;
    const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
      <div className={s.wrap}>
        {head}
        <div role="group" aria-labelledby={name} className={s.chips}>
          {options.map((o) => <Chip key={o.value} variant="toggle" icon={o.icon} label={<bdi>{o.label}</bdi>} pressed={value.includes(o.value)} onToggle={() => toggle(o.value)} />)}
        </div>
      </div>
    );
  }

  const { value, onChange, allowNone } = props;
  const at = options.findIndex((o) => o.value === value);
  const tabStop = at < 0 ? 0 : at;
  const radios = () => Array.from(group.current?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []);
  const choose = (i: number) => {
    const n = (i + options.length) % options.length;
    onChange(options[n].value);
    radios()[n]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const from = radios().indexOf(e.target as HTMLElement);
    if (from < 0) return;
    const forward = isRtl ? "ArrowLeft" : "ArrowRight";
    const back = isRtl ? "ArrowRight" : "ArrowLeft";
    const go = (i: number) => { e.preventDefault(); choose(i); };
    if (e.key === forward || e.key === "ArrowDown") go(from + 1);
    else if (e.key === back || e.key === "ArrowUp") go(from - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(options.length - 1);
  };
  return (
    <div className={s.wrap}>
      {head}
      <div ref={group} role="radiogroup" aria-labelledby={name} className={s.chips} onKeyDown={onKeyDown}>
        {options.map((o, i) => (
          <Chip key={o.value} variant="radio" icon={o.icon} label={<bdi>{o.label}</bdi>} checked={o.value === value} tabIndex={i === tabStop ? 0 : -1} onSelect={() => onChange(allowNone && o.value === value ? "" : o.value)} />
        ))}
      </div>
    </div>
  );
};

